import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div 
      className="border-2 border-slate-100 bg-white hover:border-orange-500/30 transition-colors"
      data-testid="faq-item"
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-6 text-left"
        aria-expanded={isOpen}
      >
        <h3 className="font-heading text-lg md:text-xl font-bold text-slate-900 pr-4">
          {question}
        </h3>
        <div className={`flex-shrink-0 w-10 h-10 bg-orange-500 flex items-center justify-center transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-5 h-5 text-white" />
        </div>
      </button>
      
      {isOpen && (
        <div className="px-6 pb-6">
          <p className="text-slate-600 leading-relaxed">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    fetchFAQ();
  }, []);

  const fetchFAQ = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqData(response.data);
    } catch (err) {
      console.error('Failed to fetch FAQ', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="faq-page">
      {/* Header */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
            Common Questions
          </p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            FAQ
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Find answers to frequently asked questions about our boats, 
            build process, and what makes us different.
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => handleClick(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
            We're happy to help. Reach out and we'll get back to you as soon as possible.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
            Contact Us
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
